import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  Stack,
  Divider
} from '@mui/material';
import { tokens } from '../../theme';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MergeIcon from '@mui/icons-material/Merge';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { combineFitFiles } from '../../api/runningApi';

const FitFileCombiner = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleFile1Change = (event) => {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.fit')) {
      setFile1(file);
      setError(null);
      setSuccess(null);
    } else {
      setError('Please select a valid FIT file');
      event.target.value = '';
    }
  };

  const handleFile2Change = (event) => {
    const file = event.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.fit')) {
      setFile2(file);
      setError(null);
      setSuccess(null);
    } else {
      setError('Please select a valid FIT file');
      event.target.value = '';
    }
  };

  const handleCombineFiles = async () => {
    if (!file1 || !file2) {
      setError('Please select both FIT files');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const blob = await combineFitFiles(file1, file2);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `combined_${file1.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('FIT files combined successfully! Download started.');
    } catch (err) {
      console.error('Failed to combine FIT files:', err);
      setError(err.response?.data?.detail || 'Failed to combine FIT files');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFile1(null);
    setFile2(null);
    setError(null);
    setSuccess(null);
    // Reset file inputs
    const input1 = document.getElementById('fit-file-1-input');
    const input2 = document.getElementById('fit-file-2-input');
    if (input1) input1.value = '';
    if (input2) input2.value = '';
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backgroundColor: colors.grey[800],
        borderRadius: '8px'
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <MergeIcon sx={{ mr: 1, color: colors.greenAccent[500] }} />
        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
          Combine FIT Files
        </Typography>
      </Box>

      <Typography variant="body2" color={colors.grey[300]} mb={3}>
        Upload two FIT files to combine them into a single workout. Useful when your watch stops mid-race and creates a new workout when restarted.
      </Typography>

      <Divider sx={{ mb: 3, borderColor: colors.grey[700] }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* First FIT File */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} mb={1}>
            First FIT File (Part 1) *
          </Typography>
          <input
            accept=".fit"
            style={{ display: 'none' }}
            id="fit-file-1-input"
            type="file"
            onChange={handleFile1Change}
          />
          <label htmlFor="fit-file-1-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFileIcon />}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderColor: colors.grey[600],
                color: colors.grey[100],
                '&:hover': {
                  borderColor: colors.greenAccent[500],
                  backgroundColor: colors.grey[700]
                }
              }}
            >
              {file1 ? file1.name : 'Select First FIT File'}
            </Button>
          </label>
        </Box>

        {/* Second FIT File */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} mb={1}>
            Second FIT File (Part 2) *
          </Typography>
          <input
            accept=".fit"
            style={{ display: 'none' }}
            id="fit-file-2-input"
            type="file"
            onChange={handleFile2Change}
          />
          <label htmlFor="fit-file-2-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFileIcon />}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderColor: colors.grey[600],
                color: colors.grey[100],
                '&:hover': {
                  borderColor: colors.greenAccent[500],
                  backgroundColor: colors.grey[700]
                }
              }}
            >
              {file2 ? file2.name : 'Select Second FIT File'}
            </Button>
          </label>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            onClick={handleCombineFiles}
            disabled={!file1 || !file2 || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <MergeIcon />}
            sx={{
              flex: 1,
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              '&:hover': {
                backgroundColor: colors.greenAccent[700]
              },
              '&:disabled': {
                backgroundColor: colors.grey[700],
                color: colors.grey[500]
              }
            }}
          >
            {loading ? 'Combining...' : 'Combine & Download'}
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={loading}
            startIcon={<DeleteIcon />}
            sx={{
              borderColor: colors.grey[600],
              color: colors.grey[100],
              '&:hover': {
                borderColor: colors.redAccent[500],
                backgroundColor: colors.grey[700]
              }
            }}
          >
            Clear
          </Button>
        </Box>
      </Stack>

      <Box mt={3} p={2} sx={{ backgroundColor: colors.grey[900], borderRadius: '4px' }}>
        <Typography variant="caption" color={colors.grey[400]} display="block" mb={1}>
          <strong>How it works:</strong>
        </Typography>
        <Typography variant="caption" color={colors.grey[400]} display="block">
          • The files will be combined in order: File 1 → File 2
        </Typography>
        <Typography variant="caption" color={colors.grey[400]} display="block">
          • Timestamps will be adjusted to create a continuous workout
        </Typography>
        <Typography variant="caption" color={colors.grey[400]} display="block">
          • All data (GPS, heart rate, cadence, etc.) will be preserved
        </Typography>
      </Box>
    </Paper>
  );
};

export default FitFileCombiner;
